import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Feather, MessageCircle, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { tagStyle } from "@/lib/tags";
import Seo from "@/components/Seo";
import { absoluteImageUrl } from "@/components/ImageUploader";
import CoffeeBackground from "@/components/CoffeeBackground";

const THEME_BG = {
  pink: "bg-[var(--tint-red)]",
  mint: "bg-[var(--tint-milk)]",
  lavender: "bg-[var(--tint-wine)]",
  yellow: "bg-[var(--tint-copper)]",
};
const THEME_LABEL = {
  pink: "text-[var(--tone-red)]",
  mint: "text-[var(--tone-milk)]",
  lavender: "text-[var(--tone-wine)]",
  yellow: "text-[var(--tone-copper)]",
};
const THEME_TOP = {
  pink: "border-t-[var(--tone-red)]",
  mint: "border-t-[var(--tone-milk)]",
  lavender: "border-t-[var(--tone-wine)]",
  yellow: "border-t-[var(--tone-copper)]",
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

const CATEGORY_ORDER = [
  "Psihologie & oameni",
  "Viața de adult",
  "Relații",
  "Internet, social media & cultură",
  "Bani, muncă & carieră",
  "Lifestyle & viața de zi cu zi",
  "Cărți, filme & cultură pop",
];
const DEFAULT_CATEGORY = "Viața de adult";

// Nume scurte, prietenoase pentru descrierea SEO — construită automat din categoriile active.
const CATEGORY_SEO_NAMES = {
  "Psihologie & oameni": "psihologie și oameni",
  "Viața de adult": "viața de adult",
  "Relații": "relații",
  "Internet, social media & cultură": "internet și social media",
  "Bani, muncă & carieră": "bani, muncă și carieră",
  "Lifestyle & viața de zi cu zi": "viața de zi cu zi",
  "Cărți, filme & cultură pop": "cărți, filme și cultură pop",
};
const FALLBACK_SEO_DESCRIPTION =
  "Păreri spuse pe șleau despre oameni, relații, psihologie și viața de adult — fără menajamente, fără clișee, de la Alexia.";

const HeroSection = ({ settings }) => {
  const heroImage = settings.hero_image || "";
  return (
  <section className="relative overflow-hidden bg-[var(--deep)]">
    <CoffeeBackground slot="home" tone="dark" testId="hero-bg" />
    <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 md:grid-cols-12 md:gap-16 md:py-28">
      <div className="md:col-span-7 animate-fade-up">
        <p className="mb-6 text-xs uppercase tracking-[0.35em] text-[var(--sun-light)]" data-testid="hero-eyebrow">
          {settings.hero_kicker || "Blogul Alexiei"}
        </p>
        {settings.hero_title ? (
          <h1 className="font-serif text-5xl font-light leading-[1.08] tracking-tight text-[#F8F3EC] sm:text-6xl md:text-7xl">
            {settings.hero_title}
          </h1>
        ) : (
          <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-[#F8F3EC] sm:text-6xl md:text-7xl">
            Lucrurile pe care
            <br />
            <span className="italic text-[var(--sun-light)]">nimeni nu ți le spune</span>
            <br />
            în față.
          </h1>
        )}
        <p className="mt-8 max-w-lg text-lg font-light leading-relaxed text-[#F8F3EC]/70">
          {settings.hero_subtitle ||
            "Despre relații, oameni, bani, viața de adult și lumea în care trăim cu telefonul în mână. Fără „depinde”, fără „fiecare cu părerea lui”. Aici spun ce cred, cu argumente și fără mănuși — iar dacă te deranjează, probabil am atins ceva."}
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href="#writings"
            data-testid="hero-read-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 active:scale-95"
          >
            Citește însemnările
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.6} />
          </a>
          <Link
            to="/about"
            data-testid="hero-about-link"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#F8F3EC]/80 link-flourish hover:text-[#F8F3EC]"
          >
            Cine scrie aici
          </Link>
        </div>
      </div>

      <div className="relative md:col-span-5">
        <div className="relative mx-auto aspect-[297/210] w-full max-w-lg">
          <div className="absolute inset-0 -rotate-2 rounded-[calc(var(--frame,1.5rem)+0.4rem)] bg-[var(--accent)]" />
          <div className="grain absolute inset-2 rotate-1 overflow-hidden rounded-[var(--frame,1.5rem)] shadow-lg shadow-black/30">
            {absoluteImageUrl(heroImage) ? (
              <img
                src={absoluteImageUrl(heroImage)}
                alt="Alexia, autoarea blogului"
                className="h-full w-full object-cover"
                loading="eager"
              />
            ) : (
              <div className="h-full w-full bg-[color-mix(in_srgb,var(--coral)_40%,transparent)]" />
            )}
          </div>
          <div className="absolute -bottom-5 -left-4 rotate-[-4deg] rounded-2xl bg-[var(--milk)] px-4 py-3 shadow-lg ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1A120D]/80">
              <Feather className="h-3.5 w-3.5 text-[var(--accent)]" strokeWidth={1.5} />
              câte o însemnare, din când în când
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

const PostCard = ({ post, featured = false }) => {
  const bg = THEME_BG[post.color_theme] || THEME_BG.pink;
  const label = THEME_LABEL[post.color_theme] || THEME_LABEL.pink;
  const top = THEME_TOP[post.color_theme] || THEME_TOP.pink;
  return (
    <Link
      to={`/post/${post.slug}`}
      data-testid={`post-card-${post.slug}`}
      className={`group relative flex flex-col overflow-hidden rounded-3xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] border-t-[3px] ${top} ${bg} p-6 transition-all duration-500 hover:-translate-y-1 sm:p-8 ${
        featured ? "md:col-span-8" : "md:col-span-4"
      }`}
    >
      {post.cover_image && (
        <div className={`mb-6 overflow-hidden rounded-[var(--frame,1rem)] ${featured ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
          <img
            src={post.cover_image}
            alt={post.cover_alt || ""}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <span className={`mb-3 text-xs uppercase tracking-[0.28em] ${label}`}>
        {fmt(post.created_at)}
      </span>
      <h2
        className={`font-serif font-medium tracking-tight text-[#F7F1E8] ${
          featured ? "text-3xl sm:text-4xl" : "text-2xl"
        }`}
      >
        {post.title}
      </h2>
      {post.excerpt && (
        <p className="mt-3 line-clamp-3 text-[15px] font-light leading-relaxed text-[#F7F1E8]/86">
          {post.excerpt}
        </p>
      )}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {post.tags.slice(0, 3).map((t) => (
            <span
              key={t}
              data-testid={`post-tag-${t}`}
              className={`rounded-full px-3 py-1 text-[11px] font-medium ${tagStyle(t)}`}
            >
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 group-hover:text-[#F7F1E8]">
          Citește mai departe <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.6} />
        </span>
        {post.comment_count > 0 && (
          <span
            data-testid={`post-card-notes-${post.slug}`}
            className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-[#F7F1E8]/68"
          >
            <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.6} />
            {post.comment_count} {post.comment_count === 1 ? "notă" : "note"}
          </span>
        )}
      </div>
    </Link>
  );
};

export const HomePage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [heroImage, setHeroImage] = useState("");
  const [siteSettings, setSiteSettings] = useState({});
  const [activeTag, setActiveTag] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get("/posts")
      .then((r) => setPosts(r.data))
      .catch(() => setError("Nu am putut aduce însemnările."))
      .finally(() => setLoading(false));
    api
      .get("/site-settings")
      .then((r) => {
        setHeroImage(r.data.hero_image || "");
        setSiteSettings(r.data);
      })
      .catch(() => {});
  }, []);

  const allTags = useMemo(() => {
    // Etichetele cele mai relevante: cele folosite în cele mai multe însemnări.
    // Se recalculează automat de fiecare dată când apare o însemnare nouă.
    const counts = new Map();
    posts.forEach((p) => (p.tags || []).forEach((t) => {
      const key = t.toLowerCase();
      const entry = counts.get(key) || { label: t, count: 0 };
      entry.count += 1;
      counts.set(key, entry);
    }));
    return [...counts.values()]
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ro"))
      .slice(0, 8)
      .map((e) => e.label);
  }, [posts]);

  const visiblePosts = useMemo(() => {
    let list = activeTag
      ? posts.filter((p) => (p.tags || []).some((t) => t.toLowerCase() === activeTag.toLowerCase()))
      : posts;
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const haystack = [
          p.title,
          p.excerpt || "",
          (p.content || "").replace(/<[^>]+>/g, " "),
          (p.tags || []).join(" "),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return list;
  }, [posts, activeTag, query]);

  const isFiltering = Boolean(query.trim() || activeTag);

  // SEO automat: descrierea pentru Google se construiește din categoriile care au însemnări.
  const seoDescription = useMemo(() => {
    const active = CATEGORY_ORDER.filter((c) =>
      posts.some((p) => (CATEGORY_ORDER.includes(p.category) ? p.category : DEFAULT_CATEGORY) === c)
    ).map((c) => CATEGORY_SEO_NAMES[c]);
    if (active.length === 0) return FALLBACK_SEO_DESCRIPTION;
    const listStr =
      active.length > 1 ? `${active.slice(0, -1).join(", ")} și ${active[active.length - 1]}` : active[0];
    return `Păreri spuse pe șleau despre ${listStr} — fără menajamente, fără clișee, de la Alexia.`;
  }, [posts]);

  const groupedByCategory = useMemo(() => {
    const map = new Map();
    visiblePosts.forEach((p) => {
      const c = CATEGORY_ORDER.includes(p.category) ? p.category : DEFAULT_CATEGORY;
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(p);
    });
    return CATEGORY_ORDER.filter((c) => map.has(c)).map((c) => ({ name: c, posts: map.get(c) }));
  }, [visiblePosts]);

  // Venit din firimiturile unei însemnări (/?categorie=...): derulăm la secțiunea categoriei.
  const [searchParams] = useSearchParams();
  const wantedCategory = searchParams.get("categorie");
  useEffect(() => {
    if (!wantedCategory || loading) return;
    const gi = groupedByCategory.findIndex((g) => g.name === wantedCategory);
    if (gi === -1) return;
    const t = setTimeout(() => document.getElementById(`cat-${gi}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [wantedCategory, loading, groupedByCategory]);

  return (
    <main data-testid="home-page">
      <Seo description={seoDescription} />
      <HeroSection settings={{ ...siteSettings, hero_image: heroImage }} />

      <section id="writings" className="relative overflow-hidden">
        <CoffeeBackground slot="writings" testId="writings-bg" />
        <div className="relative mx-auto max-w-6xl px-6 pb-24">
        <div className="mb-12 flex items-end justify-between border-b border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pb-6 pt-14">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-[var(--accent)]">Jurnalul</p>
            <h2 className="mt-3 font-serif text-3xl font-light text-[#F7F1E8] sm:text-4xl">
              Însemnări recente
            </h2>
          </div>
          <p className="hidden text-sm text-[#F7F1E8]/68 sm:block">
            {visiblePosts.length} {visiblePosts.length === 1 ? "însemnare" : "însemnări"}
          </p>
        </div>

        <div className="mb-8 flex max-w-md items-center gap-2 rounded-full border border-[color-mix(in_srgb,var(--milk)_15%,transparent)] bg-[#2B1B13] px-4 py-2.5 focus-within:border-[var(--accent)] focus-within:ring-2 focus-within:ring-[var(--tint-pink)]" data-testid="search-bar">
          <Search className="h-4 w-4 shrink-0 text-[#F7F1E8]/58" strokeWidth={1.6} />
          <input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Caută în însemnări…"
            className="w-full border-none bg-transparent text-sm outline-none placeholder:text-[#F7F1E8]/58"
            aria-label="Caută în însemnări"
          />
          {query && (
            <button
              data-testid="search-clear-btn"
              onClick={() => setQuery("")}
              className="rounded-full p-1 text-[#F7F1E8]/58 hover:bg-[var(--tint-pink)] hover:text-[#F7F1E8]"
              aria-label="Șterge căutarea"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
        </div>

        {allTags.length > 0 && (
          <div className="mb-10 flex flex-wrap items-center gap-2" data-testid="tag-filter-row">
            <button
              data-testid="tag-filter-all"
              onClick={() => setActiveTag("")}
              className={`rounded-full px-4 py-1.5 text-xs font-medium uppercase tracking-[0.12em] transition-colors ${
                !activeTag
                  ? "bg-[var(--accent)] text-white"
                  : "border border-[color-mix(in_srgb,var(--milk)_15%,transparent)] text-[#F7F1E8]/78 hover:border-[color-mix(in_srgb,var(--milk)_40%,transparent)] hover:text-[#F7F1E8]"
              }`}
            >
              Toate
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                data-testid={`tag-filter-${t}`}
                onClick={() => setActiveTag(activeTag === t ? "" : t)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
                  activeTag === t
                    ? `${tagStyle(t)} ring-2 ring-[color-mix(in_srgb,var(--milk)_30%,transparent)]`
                    : `${tagStyle(t)} opacity-70 hover:opacity-100`
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div data-testid="posts-loading" className="py-20 text-center text-sm text-[#F7F1E8]/68">
            Adun paginile&hellip;
          </div>
        )}
        {error && (
          <div data-testid="posts-error" className="py-20 text-center text-sm text-red-500">{error}</div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div data-testid="posts-empty" className="rounded-3xl bg-[var(--tint-butter)] p-12 text-center">
            <p className="font-serif text-2xl text-[#F7F1E8]">E liniște pe blog deocamdată.</p>
            <p className="mt-2 text-sm text-[#F7F1E8]/78">Revino curând — cafeaua e deja la fiert.</p>
          </div>
        )}

        {!loading && posts.length > 0 && visiblePosts.length === 0 && (
          <div data-testid="posts-tag-empty" className="rounded-3xl bg-[var(--tint-butter)] p-12 text-center">
            <p className="font-serif text-2xl text-[#F7F1E8]">
              {query.trim()
                ? `Nicio însemnare nu conține „${query.trim()}".`
                : `Nicio însemnare cu eticheta „${activeTag}".`}
            </p>
            <button
              onClick={() => {
                setQuery("");
                setActiveTag("");
              }}
              className="mt-3 text-sm text-[var(--accent)] underline underline-offset-4"
            >
              Vezi toate însemnările
            </button>
          </div>
        )}

        {!loading && visiblePosts.length > 0 && isFiltering && (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
            {visiblePosts.map((p, i) => (
              <PostCard key={p.id} post={p} featured={i === 0} />
            ))}
          </div>
        )}

        {!loading && visiblePosts.length > 0 && !isFiltering && (
          <div className="space-y-16" data-testid="category-sections">
            {groupedByCategory.map((group, gi) => (
              <div key={group.name} id={`cat-${gi}`} data-testid={`category-section-${gi}`} className="scroll-mt-28">
                <div className="mb-8 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <h2 className="min-w-0 font-serif text-lg font-medium leading-snug text-[var(--milk)]">
                    {group.name}
                  </h2>
                  <div className="hidden h-px flex-1 bg-[color-mix(in_srgb,var(--milk)_18%,transparent)] sm:block" />
                  <span className="shrink-0 rounded-full bg-[var(--tint-mint)] px-3 py-1 text-[10px] font-medium uppercase tracking-[0.2em] text-[var(--teal-dark)]">
                    {group.posts.length} {group.posts.length === 1 ? "însemnare" : "însemnări"}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
                  {group.posts.map((p) => (
                    <PostCard key={p.id} post={p} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </section>

      {(siteSettings.home_gallery_1 || siteSettings.home_gallery_2) && (
        <section className="mx-auto max-w-5xl px-6 pb-20" data-testid="home-gallery">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            {siteSettings.home_gallery_1 && (
              <div className="w-full max-w-sm -rotate-2 rounded-2xl bg-[#2B1B13] p-3 pb-8 shadow-xl shadow-black/10 ring-1 ring-[color-mix(in_srgb,var(--milk)_14%,transparent)] transition-transform duration-500 hover:rotate-0">
                <img
                  src={absoluteImageUrl(siteSettings.home_gallery_1)}
                  alt="Poză aleasă de Alexia"
                  className="aspect-[4/3] w-full rounded-xl object-cover"
                  loading="lazy"
                />
              </div>
            )}
            {siteSettings.home_gallery_2 && (
              <div className="w-full max-w-sm rotate-2 rounded-2xl bg-[#2B1B13] p-3 pb-8 shadow-xl shadow-black/10 ring-1 ring-[color-mix(in_srgb,var(--milk)_14%,transparent)] transition-transform duration-500 hover:rotate-0">
                <img
                  src={absoluteImageUrl(siteSettings.home_gallery_2)}
                  alt="Poză aleasă de Alexia"
                  className="aspect-[4/3] w-full rounded-xl object-cover"
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {siteSettings.home_bottom_image && (
        <section className="mx-auto max-w-6xl px-6 pb-20" data-testid="home-bottom-image">
          <div className="overflow-hidden rounded-[var(--frame,1.5rem)] shadow-lg shadow-black/10">
            <img
              src={absoluteImageUrl(siteSettings.home_bottom_image)}
              alt="Imagine aleasă de Alexia"
              className="h-auto w-full object-cover"
              loading="lazy"
            />
          </div>
        </section>
      )}
    </main>
  );
};

export default HomePage;
